import { inject, Injectable, Injector, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { LocalStorageService } from '../services/common/local-storage.service';
import { BehaviorSubject, catchError, filter, Observable, retry, switchMap, take, throwError, timeout, TimeoutError } from 'rxjs';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import { ActiveUserService, CustomerAccessInput, LanguageCode, RefreshService, PlatformService } from '@sgm-frontend/core';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private injector: Injector,
     private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('REQUEST') private request?: any,
    
  ) {

  }
  localStorageService = inject(LocalStorageService);
  activeUserService = inject(ActiveUserService);
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // ── FIX: shared in-flight guard for orphaned-user (404) recovery ─────────
  // Without this, every concurrent request that hits the orphaned-user error
  // (cart, wishlist, addresses, product-variant-ids, etc.) independently
  // calls getGuestTokens(), causing N parallel refresh calls instead of 1.
  // This mirrors the isRefreshing/refreshTokenSubject pattern already used
  // for the 401 path, but kept separate since the trigger condition differs.
  private isHandlingOrphanedUser = false;
  private orphanedUserTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // ── FIX: helper to detect a stale/orphaned token from the backend ────────
  // Backend returns this as a 404 (not 401) when the JWT is structurally
  // valid but the user it points to no longer exists in this environment's DB
  // (e.g. token issued against dev/stag DB, or account deleted).
  // Without this check, handle401Error() never fires for these errors since
  // it's gated on error.status === 401, and the navigation/resolver fails
  // silently with no console error — manifesting as a blank page.
  private isOrphanedUserError(error: HttpErrorResponse): boolean {
    const message = (error?.error?.message || '').toString().toLowerCase();
    return error.status === 404 && message.includes('user not found');
  }

  private getLanguage(): LanguageCode {
    if (this.isBrowser()) {
      return this.getBrowserLanguage();
    }

    const requestUrlLanguage = this.getLanguageFromPath(this.request?.url);
    if (requestUrlLanguage) {
      return requestUrlLanguage;
    }

    const cookieLanguage = this.getLanguageFromCookie(this.request?.headers?.cookie);
    if (cookieLanguage) {
      return cookieLanguage;
    }

    const acceptLanguage = this.request?.headers?.['accept-language'];
    if (typeof acceptLanguage === 'string') {
      return acceptLanguage.toLowerCase().includes('ar')
        ? LanguageCode.Arabic
        : LanguageCode.English;
    }

    return LanguageCode.English;
  }

  private getBrowserLanguage(): LanguageCode {
    const navigation = this.router.getCurrentNavigation();
    const navigationUrl =
      navigation?.finalUrl?.toString() ??
      navigation?.extractedUrl?.toString() ??
      '';
    const pendingNavigationLanguage = this.getLanguageFromPath(navigationUrl);

    if (pendingNavigationLanguage) {
      return pendingNavigationLanguage;
    }

    const currentPathLanguage = this.getLanguageFromPath(window.location.pathname);
    if (currentPathLanguage) {
      return currentPathLanguage;
    }

    try {
      const storedLanguage = this.getValidLanguage(localStorage.getItem('app_language'));
      if (storedLanguage) {
        return storedLanguage;
      }
    } catch {
      // Ignore storage access failures and fall through to English.
    }

    return LanguageCode.English;
  }

  private getLanguageFromCookie(cookieHeader?: string): LanguageCode | null {
    if (!cookieHeader) {
      return null;
    }

    const match = cookieHeader.match(/(?:^|;\s*)app_language=(en|ar)(?:;|$)/);
    return this.getValidLanguage(match?.[1] ?? null);
  }

  private getLanguageFromPath(url?: string): LanguageCode | null {
    if (!url) {
      return null;
    }

    let pathOnly = String(url);

    try {
      pathOnly = new URL(pathOnly, 'http://localhost').pathname;
    } catch {
      pathOnly = pathOnly.split('?')[0];
    }

    const firstSegment = pathOnly.split('/').filter(Boolean)[0]?.toLowerCase() ?? null;
    return this.getValidLanguage(firstSegment);
  }

  private getValidLanguage(value: string | null | undefined): LanguageCode | null {
    if (value === 'ar') {
      return LanguageCode.Arabic;
    }

    if (value === 'en') {
      return LanguageCode.English;
    }

    return null;
  }


  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // Analytics ingest uses its own X-Analytics-Key header — pass through untouched
    if (req.url.includes('/analytics/events')) {
      return next.handle(req);
    }

    let deviceToken = '';
    let token: string | null = null;

    // Skip auth-required endpoints on server to prevent 401 errors
    if (!this.isBrowser()) {
      console.log('[SSR] HTTP Request:', req.method, req.url);
      const authRequiredEndpoints = ['/wishlist', '/cart', '/account', '/orders', '/profile', '/addresses'];
      const isAuthRequired = authRequiredEndpoints.some(endpoint => req.url.includes(endpoint));

      if (isAuthRequired) {
        console.log('[SSR] Blocking auth-required endpoint:', req.url);
        // Return completed error observable immediately for auth-required endpoints
        // This prevents SSR from trying to fetch user-specific data
        return throwError(() => new HttpErrorResponse({
          error: { message: 'Auth required - skipped on server' },
          status: 401,
          statusText: 'Unauthorized'
        }));
      }
    }

    if (this.isBrowser()) {
      // Browser: Get from localStorage
      deviceToken = this.localStorageService.getDiviceToken() || '';
      token = this.localStorageService.getToken();
    } else {
      // Server: Try to get from cookies/headers
      if (this.request?.headers?.cookie) {
        const cookies = this.parseCookies(this.request.headers.cookie);
        token = cookies.accessToken || cookies.GaccessToken || null;
        deviceToken = cookies.deviceToken || '';
      }
      
      // Also check Authorization header if present
      if (!token && this.request?.headers?.authorization) {
        const authHeader = this.request.headers.authorization;
        if (authHeader.startsWith('Bearer ')) {
          token = authHeader.substring(7);
        }
      }
      
      // Generate device token on server if missing
      if (!deviceToken) {
        deviceToken = this.generateDeviceToken();
      }
    }

    const lang = this.getLanguage();

    const authReq = req;
    let authReqH = req;
    if (token) {
      authReqH = req.clone({
        headers: req.headers
          .set('Authorization', `Bearer ${token}`)
          .set('Device-Token', deviceToken)
          .set('Accept-Language', lang)
      });

    }
    else {
      authReqH = req.clone({
        headers: req.headers.set('Device-Token', deviceToken)
          .set('Accept-Language', lang)
      });

    }
    this.activeUserService.updateGuestStatus();
    // Skip adding Authorization header for refresh token API
    if (req.url.includes('auth/refresh') || req.url.includes('auth/login') || req.url.includes('auth/forgot-password/update')) {
      return next.handle(authReq).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401 && !req.url.includes('auth/login') && !req.url.includes('auth/forgot-password/update')) {
            console.log('interceptor error', error,'interceptor message',error.message);
            if(error?.message && error.message.toLowerCase().includes('expired')||error.message.toLowerCase().includes('token missing')){
              return this.handle401Error(req, next);
            }
            
          }
          return throwError(() => error);
        })
      );
      // return next.handle(authReq);
    }
    // Add timeout for SSR requests to prevent hanging
    const request$ = next.handle(authReqH).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && !req.url.includes('auth/login') && !req.url.includes('auth/forgot-password/update')) {

          // ── FIX: handle stale/orphaned token (404 "User not found") ──────
          // Same recovery path as an expired token: clear stale credentials
          // and fall through to guest token flow so the resolver/guard gets
          // a valid (even if guest) response instead of a silent failure.
          if (this.isOrphanedUserError(error)) {
            console.warn('[AuthInterceptor] Stale/orphaned token detected (User not found). Clearing credentials and falling back to guest flow:', req.url);
            return this.handleOrphanedUserError(req, next);
          }

          if (error.status === 401) {
            console.log('interceptor error', error,'interceptor message',error.error.message);
            if(error?.error?.message && error?.error?.message.toLowerCase().includes('expired') || error?.error?.message.toLowerCase().includes('token missing')){
                return this.handle401Error(req, next);
              }
          }
        }
        return throwError(() => error);
      })
    );

    // Add timeout only on server to prevent SSR hanging
    if (!this.isBrowser()) {
      return request$.pipe(
        timeout(8000), // 8 second timeout for SSR (some endpoints like category filter are slow)
        catchError(err => {
          if (err instanceof TimeoutError) {
            console.error('[SSR] HTTP request timeout:', req.url);
          }
          return throwError(() => err);
        })
      );
    }

    return request$;

  }

  // ── FIX: recovery path for orphaned/stale token — DEDUPED ────────────────
  // When several requests (cart, wishlist, addresses, product-variant-ids...)
  // fail with the orphaned-user error around the same time, only the FIRST
  // one actually clears tokens and calls getGuestTokens(). Every other
  // concurrent caller instead waits on orphanedUserTokenSubject and replays
  // its own request with the token the first call obtained — exactly one
  // refresh network call instead of N, while still retrying every failed
  // request afterward.
  private handleOrphanedUserError(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isBrowser()) {
      // On server, just let it fail — SSR already treats auth-required
      // endpoints as skip-on-server (see top of intercept()).
      return next.handle(req);
    }

    const deviceToken = this.localStorageService.getDiviceToken() || this.generateDeviceToken();

    // ── Already in progress: wait for the in-flight refresh to finish ─────
    if (this.isHandlingOrphanedUser) {
      return this.orphanedUserTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const clonedReq = req.clone({
            headers: req.headers
              .set('Authorization', `Bearer ${token}`)
              .set('Device-Token', deviceToken)
          });
          return next.handle(clonedReq);
        })
      );
    }

    // ── First caller: do the actual refresh ────────────────────────────────
    this.isHandlingOrphanedUser = true;
    this.orphanedUserTokenSubject.next(null);

    // Clear the bad/orphaned tokens so nothing re-sends them.
    this.localStorageService.clearTokens();
    this.localStorageService.setDiviceToken(deviceToken);

    const refreshService = this.injector.get(RefreshService);

    return refreshService.getGuestTokens().pipe(
      retry(3),
      switchMap(() => {
        const newAccessToken = this.localStorageService.getRGToken();

        if (newAccessToken) {
          this.isHandlingOrphanedUser = false;
          this.orphanedUserTokenSubject.next(newAccessToken);

          const clonedReq = req.clone({
            headers: req.headers
              .set('Authorization', `Bearer ${newAccessToken}`)
              .set('Device-Token', deviceToken)
          });
          return next.handle(clonedReq);
        }

        this.isHandlingOrphanedUser = false;
        return throwError(() => new Error('Failed to obtain guest token after orphaned-user fallback.'));
      }),
      catchError((finalErr) => {
        this.isHandlingOrphanedUser = false;
        // Last resort — give up and propagate the original-shaped error
        // so any UI-level error handling further up the chain still works.
        console.error('[AuthInterceptor] Guest token fallback failed after orphaned-user error:', finalErr);
        return throwError(() => finalErr);
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token refresh on server - not applicable for SSR
    if (!this.isBrowser()) {
      return next.handle(req);
    }

    const deviceToken = this.localStorageService.getDiviceToken() || '';
    const refreshService = this.injector.get(RefreshService); // Lazy injection here

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      let refreshToken: CustomerAccessInput = { refreshToken: '' };
      if (this.localStorageService.checkIsGuest()&& !this.localStorageService.getRGToken()) {
        refreshToken = { refreshToken: this.localStorageService.getRGToken() };
         return refreshService.getGuestTokens().pipe(
              retry(3),
              switchMap(() => {
                const newAccessToken = this.localStorageService.getRGToken();

                if (newAccessToken) {
                  this.refreshTokenSubject.next(newAccessToken);
                  const clonedReq = req.clone({
                    headers: req.headers
                      .set('Authorization', `Bearer ${newAccessToken}`)
                      .set('Device-Token', deviceToken)
                  });
                  return next.handle(clonedReq);
                } else {
                  return throwError(() => new Error('Failed to obtain guest token after 401.'));
                }
              }),
              catchError(finalErr => throwError(() => finalErr))
              );
        
      } else if (this.localStorageService.checkIsGuest()&& this.localStorageService.getRGToken()) {
        refreshToken = { refreshToken: this.localStorageService.getRGToken() };
      }else {
        refreshToken = { refreshToken: this.localStorageService.getRToken() };
      }

      return refreshService.refreshAccessToken(refreshToken).pipe(
        retry(3), // Retry refresh token 3 times
        switchMap((res: any) => {
          this.isRefreshing = false;
          const newAccessToken = res.accessToken;
          this.refreshTokenSubject.next(newAccessToken);

          const clonedReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`)
              .set('Device-Token', deviceToken)
          });
          return next.handle(clonedReq);
        }),
        catchError((err) => {
          this.isRefreshing = false;

          if (this.localStorageService.checkIsGuest()) {
            // Retry getGuestTokens 3 times
            return refreshService.getGuestTokens().pipe(
              retry(3),
              switchMap(() => {
                const newAccessToken = this.localStorageService.getRGToken();

                if (newAccessToken) {
                  this.refreshTokenSubject.next(newAccessToken);
                  const clonedReq = req.clone({
                    headers: req.headers
                      .set('Authorization', `Bearer ${newAccessToken}`)
                      .set('Device-Token', deviceToken)
                  });
                  return next.handle(clonedReq);
                } else {
                  return throwError(() => new Error('Failed to obtain guest token after 401.'));
                }
              }),
              catchError((innerErr) => {
                // Final fallback after guest token retries failed
                this.isRefreshing = false;

                // 👉 regenerate device token before retry
                const newDeviceToken = this.generateDeviceToken();
                this.localStorageService.setDiviceToken(newDeviceToken);

                return refreshService.getGuestTokens().pipe(
                  switchMap(() => {
                    const newAccessToken = this.localStorageService.getRGToken();
                    if (newAccessToken) {
                      this.refreshTokenSubject.next(newAccessToken);
                      const clonedReq = req.clone({
                        headers: req.headers
                          .set('Authorization', `Bearer ${newAccessToken}`)
                          .set('Device-Token', newDeviceToken) // use refreshed device token
                      });
                      return next.handle(clonedReq);
                    } else {
                      return throwError(() => new Error('Failed to obtain guest token after fallback.'));
                    }
                  }),
                  catchError(finalErr => throwError(() => finalErr))
                );
              })
            );
          } else {
            this.localStorageService.clearTokens();
          }
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const clonedReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`)
              .set('Device-Token', deviceToken)
          });
          return next.handle(clonedReq);
        })
      );
    }
  }
  newDiviceToken(){ 
    if (!this.isBrowser()) return;
    const token = this.generateDeviceToken(); 
    if(!this.localStorageService.getDiviceToken()){ 
      this.localStorageService.setDiviceToken(token); 
    }
  }
   generateDeviceToken(length = 20): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token2 = '';
  for (let i = 0; i < length; i++) {
    token2 += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token2;
}

  private parseCookies(cookieHeader: string): Record<string, string> {
    const cookies: Record<string, string> = {};
    if (!cookieHeader) return cookies;
    
    cookieHeader.split(';').forEach(cookie => {
      const parts = cookie.trim().split('=');
      if (parts.length === 2) {
        const key = parts[0].trim();
        const value = decodeURIComponent(parts[1].trim());
        cookies[key] = value;
      }
    });
    
    return cookies;
  }
}